// pages/Agents/AgentSpace/AgentSpaceGeneralSourceCard.js
// Source configuration card for the "AI General" agent — a 3-step wizard:
//   Step 1  Input source   — where the agent READS from
//   Step 2  Output source  — where the agent WRITES the result
//   Step 3  Prompt         — one instruction driving the whole input→output task
//
// Both the input and output pickers use an "app launcher" connector grid: the
// user picks a connector tile first (Upload / Google Drive / OneDrive /
// External URL), then the connector's own fields appear. For Google Drive and
// OneDrive there are two ways to point at a file:
//   • Browse & pick — connect the account, then browse the folder tree
//   • Paste a link  — paste a share URL (input = a FILE link to read;
//                      output = a FOLDER link the result is uploaded into,
//                      replacing a same-named file)
//
// One input row + one output row per chatbot; switching a connector wipes the
// previous connector's fields.
import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    MenuItem,
    Skeleton,
    Chip,
    Tooltip,
    IconButton,
    CircularProgress,
    Breadcrumbs,
    Link,
    Stepper,
    Step,
    StepLabel,
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import AddToDriveIcon from '@mui/icons-material/AddToDrive';
import CloudIcon from '@mui/icons-material/Cloud';
import LinkIcon from '@mui/icons-material/Link';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import OutputOutlinedIcon from '@mui/icons-material/OutputOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import axiosInstance from '../../../../utils/axiosInstance';
import { useParams } from 'react-router-dom';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    textTertiary: '#94a3b8',
    accent: '#a855f7',
    success: '#059669',
    danger: '#dc2626',
    warning: '#f59e0b',
};

// Connector tiles shown in the app-launcher grid. The id IS the stored
// source_type, so Google Drive and OneDrive are their own connectors (no more
// generic "drive" with a provider sub-toggle).
const CONNECTORS = [
    { id: 'upload', label: 'Upload', description: 'From your computer', icon: <CloudUploadOutlinedIcon sx={{ fontSize: 24 }} />, color: '#a855f7' },
    { id: 'google_drive', label: 'Google Drive', description: 'Connect or paste a link', icon: <AddToDriveIcon sx={{ fontSize: 24 }} />, color: '#1a73e8' },
    { id: 'onedrive', label: 'OneDrive', description: 'Connect or paste a link', icon: <CloudIcon sx={{ fontSize: 24 }} />, color: '#0078d4' },
    { id: 'external_url', label: 'External URL', description: 'Any public web URL', icon: <LinkIcon sx={{ fontSize: 24 }} />, color: '#059669' },
];

// Maps file-format string to the right MUI icon.
const FILE_TYPE_ICONS = {
    excel: <TableChartOutlinedIcon sx={{ fontSize: 18 }} />,
    xlsx: <TableChartOutlinedIcon sx={{ fontSize: 18 }} />,
    csv: <TableChartOutlinedIcon sx={{ fontSize: 18 }} />,
    txt: <ArticleOutlinedIcon sx={{ fontSize: 18 }} />,
    doc: <ArticleOutlinedIcon sx={{ fontSize: 18 }} />,
    docx: <ArticleOutlinedIcon sx={{ fontSize: 18 }} />,
    pdf: <PictureAsPdfOutlinedIcon sx={{ fontSize: 18 }} />,
    other: <InsertDriveFileOutlinedIcon sx={{ fontSize: 18 }} />,
};

function guessFileType(filename) {
    if (!filename) return 'other';
    const n = filename.toLowerCase();
    if (n.endsWith('.xlsx') || n.endsWith('.xls') || n.endsWith('.xlsm')) return 'excel';
    if (n.endsWith('.csv')) return 'csv';
    if (n.endsWith('.txt')) return 'txt';
    if (n.endsWith('.docx')) return 'docx';
    if (n.endsWith('.doc')) return 'doc';
    if (n.endsWith('.pdf')) return 'pdf';
    return 'other';
}

function formatBytes(bytes) {
    if (bytes == null) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

// google_drive / onedrive are the two "drive" connectors that share the
// browse / paste-link machinery.
// The first "User Asks" suggestion is always this fixed action — it tells the
// agent to read the saved prompt and run the whole input→output task. Kept in
// sync (exact string) with the backend so it's never duplicated.
const RUN_TASK_QUESTION = 'Run the configured task';

const isDriveType = (t) => t === 'google_drive' || t === 'onedrive';
// Stored source_type → DrivePickerArea provider key.
const providerOf = (t) => (t === 'onedrive' ? 'onedrive' : 'google');
const rootCrumb = (provider) => [{ id: 'root', name: provider === 'onedrive' ? 'OneDrive' : 'My Drive' }];

// Pre-filled into the Step-3 task box so the user starts from a detailed,
// working example they can tweak, rather than a blank field. It spells out the
// three things to describe — what to READ, what to DO, and what to WRITE — with
// a concrete worked example so the shape of a good instruction is obvious.
const DEFAULT_OUTPUT_PROMPT =
`Run this task end to end on the configured source.

1) INPUT — what to read from the input source:
   Read the file and the columns this task needs. Name them so I don't guess —
   e.g. "read the 'Status', 'Due Date' and 'Amount' columns" (or "read the whole
   file" / "use the sheet named 'Invoices'").

2) TASK — what to find, filter and change:
   Describe the target column, the condition, and the change. Example:
   • Look at the 'Status' column and find every row marked "Overdue".
   • From those, keep only the rows where 'Amount' is greater than 50.
   • For each matching row, add a 'Days Late' value calculated from the 'Due Date'.

3) OUTPUT — what to produce:
   Apply the changes above and write the finished result to the configured output
   source — if an output file is already selected, UPDATE that file in place;
   otherwise GENERATE a new file. Keep every other column and row exactly as it
   is; only change what the task in step 2 asks for.`;

export default function AgentSpaceGeneralSourceCard({ chatbotId, onChange, onSnack }) {
    // chatbotId is also available from useParams for building connector links.
    // Prefer the prop, fall back to the URL.
    const { chatbotId: routeChatbotId } = useParams();
    const effectiveChatbotId = chatbotId || routeChatbotId;

    const [loading, setLoading] = useState(true);
    const [source, setSource] = useState(null);

    // ---- Wizard step (0 = input, 1 = output source, 2 = prompt) ----
    const [step, setStep] = useState(0);

    // ================= INPUT source form state =================
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [activeType, setActiveType] = useState('upload');     // connector id == source_type

    // Local file (Upload):
    const [pendingFile, setPendingFile] = useState(null);
    const fileInputRef = useRef(null);

    // Drive (Google/OneDrive) — browse or paste-link:
    const [driveMethod, setDriveMethod] = useState('browse');   // 'browse' | 'url'
    const [driveUrl, setDriveUrl] = useState('');               // pasted FILE link (input)
    const [driveProvider, setDriveProvider] = useState('google');
    const [driveStatus, setDriveStatus] = useState(null);       // { is_connected, email }
    const [driveStatusLoading, setDriveStatusLoading] = useState(false);
    const [drivePath, setDrivePath] = useState([{ id: 'root', name: 'My Drive' }]);
    const [driveItems, setDriveItems] = useState([]);
    const [driveListLoading, setDriveListLoading] = useState(false);
    const [drivePickedFile, setDrivePickedFile] = useState(null);

    // External URL:
    const [externalUrl, setExternalUrl] = useState('');
    const [externalFileType, setExternalFileType] = useState('other');

    const [submitting, setSubmitting] = useState(false);

    // ================= OUTPUT pipeline state =================
    const [outputEnabled, setOutputEnabled] = useState(true);
    const [outputPrompt, setOutputPrompt] = useState(DEFAULT_OUTPUT_PROMPT);
    const [outputMode, setOutputMode] = useState('same_as_input'); // 'same_as_input' | 'separate'
    const [outputSubmitting, setOutputSubmitting] = useState(false);
    const [outputSaved, setOutputSaved] = useState(null);          // last persisted output row
    // Identity for a SEPARATE output file (parallels the input title/desc).
    const [outputTitle, setOutputTitle] = useState('');
    const [outputDescription, setOutputDescription] = useState('');
    // "User Asks" — example chat questions generated from the saved prompt.
    const [userAsks, setUserAsks] = useState([]);
    const [userAsksLoading, setUserAsksLoading] = useState(false);

    // Separate OUTPUT connector picker (mirrors the input picker):
    const [oActiveType, setOActiveType] = useState('upload');
    const [oPendingFile, setOPendingFile] = useState(null);
    const oFileInputRef = useRef(null);
    const [oDriveMethod, setODriveMethod] = useState('url');       // output drive defaults to folder-link
    const [oDriveUrl, setODriveUrl] = useState('');                // pasted FOLDER link (output)
    const [oFolderFormat, setOFolderFormat] = useState('xlsx');    // format of the generated file
    const [oDriveProvider, setODriveProvider] = useState('google');
    const [oDriveStatus, setODriveStatus] = useState(null);
    const [oDriveStatusLoading, setODriveStatusLoading] = useState(false);
    const [oDrivePath, setODrivePath] = useState([{ id: 'root', name: 'My Drive' }]);
    const [oDriveItems, setODriveItems] = useState([]);
    const [oDriveListLoading, setODriveListLoading] = useState(false);
    const [oDrivePickedFile, setODrivePickedFile] = useState(null);
    const [oExternalUrl, setOExternalUrl] = useState('');
    const [oExternalFileType, setOExternalFileType] = useState('other');

    const notify = useCallback((message, severity = 'success') => {
        if (onSnack) onSnack({ open: true, message, severity });
    }, [onSnack]);

    // ----- Load current source + output config -----
    useEffect(() => {
        if (!chatbotId) return;
        let cancelled = false;
        setLoading(true);
        axiosInstance.get(`/api/agent-space/general/source?chatbot_id=${chatbotId}`)
            .then((res) => {
                if (cancelled) return;
                const data = res.data?.data || null;
                setSource(data && data.source_type ? data : null);
                if (data && data.source_type) {
                    setTitle(data.title || '');
                    setDescription(data.description || '');
                    setActiveType(data.source_type);
                    if (isDriveType(data.source_type)) {
                        setDriveProvider(providerOf(data.source_type));
                        setDrivePath(rootCrumb(providerOf(data.source_type)));
                        if (data.drive_file_id) {
                            setDriveMethod('browse');
                            setDrivePickedFile({
                                id: data.drive_file_id,
                                name: data.drive_file_name,
                                web_view_link: data.drive_file_url,
                                mime_type: data.drive_mime_type,
                                file_type: data.file_type,
                            });
                        }
                    }
                    if (data.source_type === 'external_url') {
                        setExternalUrl(data.external_url || '');
                        setExternalFileType(data.file_type || 'other');
                    }
                }
                // Hydrate output config from the nested `output` object.
                const o = (data && data.output) || null;
                setOutputSaved(o);
                if (o) {
                    setOutputEnabled(o.enabled !== undefined ? !!o.enabled : true);
                    setOutputPrompt(o.prompt || DEFAULT_OUTPUT_PROMPT);
                    setOutputMode(o.mode || 'same_as_input');
                    setOutputTitle(o.title || '');
                    setOutputDescription(o.description || '');
                    if (Array.isArray(o.example_questions)) setUserAsks(o.example_questions);
                    if (o.source_type) {
                        setOActiveType(o.source_type);
                        if (isDriveType(o.source_type)) {
                            const prov = providerOf(o.source_type);
                            setODriveProvider(prov);
                            setODrivePath(rootCrumb(prov));
                            if (o.drive_folder_id) {
                                setODriveMethod('url');
                                setODriveUrl(o.drive_folder_url || '');
                                setOFolderFormat(o.file_type || 'xlsx');
                            } else if (o.drive_file_id) {
                                setODriveMethod('browse');
                                setODrivePickedFile({
                                    id: o.drive_file_id,
                                    name: o.drive_file_name,
                                    web_view_link: o.drive_file_url,
                                    mime_type: o.drive_mime_type,
                                    file_type: o.file_type,
                                });
                            }
                        }
                        if (o.source_type === 'external_url') {
                            setOExternalUrl(o.external_url || '');
                            setOExternalFileType(o.file_type || 'other');
                        }
                    }
                }
            })
            .catch(() => { /* no source yet */ })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [chatbotId]);

    // =========================================================
    // INPUT drive: status + folder browsing
    // =========================================================
    const refreshDriveStatus = useCallback(() => {
        setDriveStatusLoading(true);
        return axiosInstance.get('/api/agent-space/general/drive/status', { params: { provider: driveProvider } })
            .then((res) => { const n = res.data?.data || null; setDriveStatus(n); return n; })
            .catch(() => { const f = { is_connected: false }; setDriveStatus(f); return f; })
            .finally(() => setDriveStatusLoading(false));
    }, [driveProvider]);

    useEffect(() => {
        if (!isDriveType(activeType) || driveMethod !== 'browse') return;
        refreshDriveStatus();
    }, [activeType, driveMethod, refreshDriveStatus]);

    const loadDriveItems = useCallback((parentId) => {
        setDriveListLoading(true);
        return axiosInstance.get('/api/agent-space/general/drive/items', {
            params: { provider: driveProvider, parent_id: parentId || 'root' },
        })
            .then((res) => setDriveItems(res.data?.data?.items || []))
            .catch((err) => {
                setDriveItems([]);
                const d = err?.response?.data?.data;
                if (d && d.is_connected === false) setDriveStatus({ is_connected: false });
            })
            .finally(() => setDriveListLoading(false));
    }, [driveProvider]);

    useEffect(() => {
        if (!isDriveType(activeType) || driveMethod !== 'browse' || !driveStatus?.is_connected) return;
        const cur = drivePath[drivePath.length - 1]?.id || 'root';
        loadDriveItems(cur);
    }, [activeType, driveMethod, driveStatus, driveProvider, drivePath, loadDriveItems]);

    const enterFolder = (item) => setDrivePath((p) => [...p, { id: item.id, name: item.name }]);
    const navigateToBreadcrumb = (idx) => setDrivePath((p) => p.slice(0, idx + 1));

    const handleConnectDrive = () => {
        if (!effectiveChatbotId) {
            notify('Missing chatbot context — open this page from a chatbot to connect a drive.', 'warning');
            return;
        }
        const connectorPath = driveProvider === 'google'
            ? `/chatbot/${effectiveChatbotId}/connectors/gdrive`
            : `/chatbot/${effectiveChatbotId}/connectors/onedrive`;
        const tab = window.open(connectorPath, '_blank', 'noopener');
        if (!tab) { notify('Pop-up blocked. Allow pop-ups and click Connect again.', 'warning'); return; }
        const intervalId = setInterval(() => {
            refreshDriveStatus().then((s) => { if (s?.is_connected) clearInterval(intervalId); });
        }, 2500);
        setTimeout(() => clearInterval(intervalId), 5 * 60 * 1000);
    };

    // Pick an INPUT connector — resets the drive sub-state for the new provider.
    const handlePickConnector = (id) => {
        setActiveType(id);
        if (isDriveType(id)) {
            const prov = providerOf(id);
            setDriveProvider(prov);
            setDrivePath(rootCrumb(prov));
            setDriveItems([]);
            setDrivePickedFile(null);
        }
    };

    const handlePickLocalFile = () => fileInputRef.current?.click();
    const handleLocalFileChange = (e) => {
        const file = e.target.files?.[0];
        if (file) setPendingFile(file);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // =========================================================
    // OUTPUT drive: parallel status + folder browsing
    // =========================================================
    const refreshODriveStatus = useCallback(() => {
        setODriveStatusLoading(true);
        return axiosInstance.get('/api/agent-space/general/drive/status', { params: { provider: oDriveProvider } })
            .then((res) => { const n = res.data?.data || null; setODriveStatus(n); return n; })
            .catch(() => { const f = { is_connected: false }; setODriveStatus(f); return f; })
            .finally(() => setODriveStatusLoading(false));
    }, [oDriveProvider]);

    useEffect(() => {
        if (outputMode !== 'separate' || !isDriveType(oActiveType) || oDriveMethod !== 'browse') return;
        refreshODriveStatus();
    }, [outputMode, oActiveType, oDriveMethod, refreshODriveStatus]);

    const loadODriveItems = useCallback((parentId) => {
        setODriveListLoading(true);
        return axiosInstance.get('/api/agent-space/general/drive/items', {
            params: { provider: oDriveProvider, parent_id: parentId || 'root' },
        })
            .then((res) => setODriveItems(res.data?.data?.items || []))
            .catch((err) => {
                setODriveItems([]);
                const d = err?.response?.data?.data;
                if (d && d.is_connected === false) setODriveStatus({ is_connected: false });
            })
            .finally(() => setODriveListLoading(false));
    }, [oDriveProvider]);

    useEffect(() => {
        if (outputMode !== 'separate' || !isDriveType(oActiveType) || oDriveMethod !== 'browse' || !oDriveStatus?.is_connected) return;
        const cur = oDrivePath[oDrivePath.length - 1]?.id || 'root';
        loadODriveItems(cur);
    }, [outputMode, oActiveType, oDriveMethod, oDriveStatus, oDriveProvider, oDrivePath, loadODriveItems]);

    const oEnterFolder = (item) => setODrivePath((p) => [...p, { id: item.id, name: item.name }]);
    const oNavigateBreadcrumb = (idx) => setODrivePath((p) => p.slice(0, idx + 1));

    const handleConnectODrive = () => {
        if (!effectiveChatbotId) { notify('Missing chatbot context.', 'warning'); return; }
        const path = oDriveProvider === 'google'
            ? `/chatbot/${effectiveChatbotId}/connectors/gdrive`
            : `/chatbot/${effectiveChatbotId}/connectors/onedrive`;
        const tab = window.open(path, '_blank', 'noopener');
        if (!tab) { notify('Pop-up blocked. Allow pop-ups and click Connect again.', 'warning'); return; }
        const id = setInterval(() => { refreshODriveStatus().then((s) => { if (s?.is_connected) clearInterval(id); }); }, 2500);
        setTimeout(() => clearInterval(id), 5 * 60 * 1000);
    };

    // Pick an OUTPUT connector. Drive connectors default to the folder-link
    // method (that's the "drive folder URL where the file uploads" flow).
    const handlePickOConnector = (id) => {
        setOActiveType(id);
        if (isDriveType(id)) {
            const prov = providerOf(id);
            setODriveProvider(prov);
            setODrivePath(rootCrumb(prov));
            setODriveItems([]);
            setODrivePickedFile(null);
        }
    };

    const handlePickOLocalFile = () => oFileInputRef.current?.click();
    const handleOLocalFileChange = (e) => {
        const file = e.target.files?.[0];
        if (file) setOPendingFile(file);
        if (oFileInputRef.current) oFileInputRef.current.value = '';
    };

    // ----- Validation + submit (INPUT) -----
    const canSubmit = () => {
        if (!title.trim()) return false;
        if (activeType === 'upload') {
            return !!pendingFile || (source?.source_type === 'upload' && !!source.file_path);
        }
        if (isDriveType(activeType)) {
            return driveMethod === 'url' ? !!driveUrl.trim() : !!drivePickedFile;
        }
        if (activeType === 'external_url') return !!externalUrl.trim();
        return false;
    };

    const handleSubmit = async ({ advance = false } = {}) => {
        if (!canSubmit()) {
            notify('Fill in the required fields before submitting.', 'warning');
            return;
        }
        setSubmitting(true);
        try {
            let resp;
            if (activeType === 'upload') {
                if (pendingFile) {
                    const form = new FormData();
                    form.append('chatbot_id', chatbotId);
                    form.append('file', pendingFile);
                    form.append('title', title.trim());
                    if (description.trim()) form.append('description', description.trim());
                    resp = await axiosInstance.post('/api/agent-space/general/upload', form, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                    });
                } else if (source?.source_type === 'upload' && source.file_path) {
                    setSubmitting(false);
                    if (advance) setStep(1);
                    return;
                } else {
                    notify('Choose a file to upload first.', 'warning');
                    setSubmitting(false);
                    return;
                }
            } else if (isDriveType(activeType)) {
                const common = {
                    chatbot_id: chatbotId,
                    source_type: activeType,
                    title: title.trim(),
                    description: description.trim() || null,
                };
                if (driveMethod === 'url') {
                    resp = await axiosInstance.post('/api/agent-space/general/source', {
                        ...common, drive_url: driveUrl.trim(),
                    });
                } else {
                    resp = await axiosInstance.post('/api/agent-space/general/source', {
                        ...common,
                        drive_file_id: drivePickedFile.id,
                        drive_file_name: drivePickedFile.name,
                        drive_file_url: drivePickedFile.web_view_link,
                        drive_mime_type: drivePickedFile.mime_type,
                        file_type: drivePickedFile.file_type || guessFileType(drivePickedFile.name),
                    });
                }
            } else if (activeType === 'external_url') {
                resp = await axiosInstance.post('/api/agent-space/general/source', {
                    chatbot_id: chatbotId,
                    source_type: 'external_url',
                    title: title.trim(),
                    description: description.trim() || null,
                    external_url: externalUrl.trim(),
                    file_type: externalFileType || guessFileType(externalUrl),
                });
            }

            if (resp?.data?.success) {
                setSource(resp.data.data);
                setPendingFile(null);
                notify('Source saved.', 'success');
                if (onChange) onChange(resp.data.data);
                if (advance) setStep(1);
            } else {
                notify(resp?.data?.message || 'Could not save source.', 'error');
            }
        } catch (e) {
            console.error('Save source failed', e);
            notify(e?.response?.data?.message || 'Could not save source.', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleRemove = async () => {
        if (!window.confirm('Remove the configured source?')) return;
        setSubmitting(true);
        try {
            const resp = await axiosInstance.delete(`/api/agent-space/general/source?chatbot_id=${chatbotId}`);
            if (resp.data?.success) {
                setSource(null);
                setTitle(''); setDescription('');
                setPendingFile(null);
                setDrivePickedFile(null);
                setDriveUrl('');
                setExternalUrl('');
                notify('Source removed.', 'success');
                if (onChange) onChange(null);
            } else {
                notify(resp.data?.message || 'Could not remove.', 'error');
            }
        } catch (e) {
            console.error(e);
            notify('Could not remove.', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    // ----- Save output pipeline config -----
    // Generate the "User Asks" example questions tailored to the saved prompt.
    const fetchUserAsks = useCallback(async (promptText) => {
        if (!chatbotId) return;
        setUserAsksLoading(true);
        try {
            const resp = await axiosInstance.post('/api/agent-space/general/suggest-questions', {
                chatbot_id: chatbotId,
                prompt: (promptText || '').trim() || undefined,
            });
            if (resp.data?.success) setUserAsks(resp.data.data?.questions || []);
        } catch (e) {
            console.error('suggest-questions failed', e);
        } finally {
            setUserAsksLoading(false);
        }
    }, [chatbotId]);

    const handleSaveOutput = async () => {
        if (outputEnabled && !outputPrompt.trim()) {
            notify('Write what the agent should produce in the output prompt.', 'warning');
            return;
        }
        const hasSavedSeparate = !!outputSaved?.source_type;
        if (outputEnabled && outputMode === 'separate' && !hasSavedSeparate) {
            if (oActiveType === 'upload' && !oPendingFile) {
                notify('Choose a file to upload for the output source.', 'warning'); return;
            }
            if (isDriveType(oActiveType)) {
                if (oDriveMethod === 'url' && !oDriveUrl.trim()) {
                    notify('Paste the drive folder link for the output.', 'warning'); return;
                }
                if (oDriveMethod === 'browse' && !oDrivePickedFile) {
                    notify('Pick a file to overwrite for the output.', 'warning'); return;
                }
            }
            if (oActiveType === 'external_url' && !oExternalUrl.trim()) {
                notify('Enter the external URL for the output source.', 'warning'); return;
            }
        }
        setOutputSubmitting(true);
        try {
            let resp;
            const base = {
                chatbot_id: chatbotId,
                enabled: outputEnabled,
                prompt: outputPrompt.trim(),
                mode: outputMode,
                title: outputTitle.trim() || null,
                description: outputDescription.trim() || null,
            };

            if (outputMode === 'separate' && oActiveType === 'upload' && oPendingFile) {
                const fd = new FormData();
                fd.append('file', oPendingFile);
                fd.append('chatbot_id', chatbotId);
                fd.append('enabled', String(outputEnabled));
                fd.append('prompt', outputPrompt.trim());
                fd.append('title', outputTitle.trim());
                fd.append('description', outputDescription.trim());
                resp = await axiosInstance.post('/api/agent-space/general/output/upload', fd, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
            } else if (outputMode === 'separate' && isDriveType(oActiveType) && oDriveMethod === 'url' && oDriveUrl.trim()) {
                resp = await axiosInstance.post('/api/agent-space/general/output', {
                    ...base,
                    source_type: oActiveType,
                    drive_url: oDriveUrl.trim(),
                    file_type: oFolderFormat || 'xlsx',
                });
            } else if (outputMode === 'separate' && isDriveType(oActiveType) && oDriveMethod === 'browse' && oDrivePickedFile) {
                resp = await axiosInstance.post('/api/agent-space/general/output', {
                    ...base,
                    source_type: oActiveType,
                    drive_file_id: oDrivePickedFile.id,
                    drive_file_name: oDrivePickedFile.name,
                    drive_file_url: oDrivePickedFile.web_view_link || null,
                    drive_mime_type: oDrivePickedFile.mime_type || null,
                    file_type: oDrivePickedFile.file_type || guessFileType(oDrivePickedFile.name),
                });
            } else if (outputMode === 'separate' && oActiveType === 'external_url' && oExternalUrl.trim()) {
                resp = await axiosInstance.post('/api/agent-space/general/output', {
                    ...base,
                    source_type: 'external_url',
                    external_url: oExternalUrl.trim(),
                    file_type: oExternalFileType || guessFileType(oExternalUrl),
                });
            } else {
                // Same-as-input, or a metadata-only update of an already-saved separate source.
                resp = await axiosInstance.post('/api/agent-space/general/output', base);
            }

            if (resp.data?.success) {
                notify('Output configuration saved.', 'success');
                const out = resp.data.data?.output || resp.data.data;
                if (out && typeof out === 'object') {
                    setOutputSaved(out);
                    setOPendingFile(null);
                }
                if (onChange) onChange(resp.data.data);
                // Generate the "User Asks" suggestions from the just-saved prompt.
                fetchUserAsks(outputPrompt);
            } else {
                notify(resp.data?.message || 'Could not save output config.', 'error');
            }
        } catch (e) {
            console.error('Save output failed', e);
            notify(e?.response?.data?.message || 'Could not save output config.', 'error');
        } finally {
            setOutputSubmitting(false);
        }
    };

    if (loading) {
        return <Skeleton variant="rounded" height={320} sx={{ borderRadius: '14px' }} />;
    }

    const isConfigured = !!source;

    // Short label for the Step-3 summary line.
    const outputSummary = () => {
        if (outputMode === 'same_as_input') return 'same file (in place)';
        if (outputTitle.trim()) return outputTitle.trim();
        if (oActiveType === 'upload') return oPendingFile?.name || outputSaved?.file_name || 'uploaded file';
        if (isDriveType(oActiveType)) {
            if (oDriveMethod === 'url') return (oDriveUrl && `${providerOf(oActiveType) === 'google' ? 'Drive' : 'OneDrive'} folder`) || outputSaved?.drive_folder_name || 'drive folder';
            return oDrivePickedFile?.name || outputSaved?.drive_file_name || 'drive file';
        }
        if (oActiveType === 'external_url') return oExternalUrl || outputSaved?.external_url || 'external URL';
        return 'separate output';
    };

    return (
        <Box sx={{
            p: 2, borderRadius: '14px',
            bgcolor: 'rgba(255,255,255,0.85)',
            border: '1px solid rgba(0,0,0,0.06)',
        }}>
            {/* ---- Header ---- */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5, gap: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontFamily, fontSize: '0.88rem', fontWeight: 700, color: colors.textPrimary }}>
                        Configure AI General
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.72rem', color: colors.textTertiary, mt: 0.25 }}>
                        Three steps: pick the input source, choose where the output goes, then write one prompt that drives the whole task.
                    </Typography>
                </Box>
                <Chip
                    size="small"
                    icon={isConfigured
                        ? <CheckCircleOutlineIcon sx={{ fontSize: '14px !important', color: `${colors.success} !important` }} />
                        : undefined}
                    label={isConfigured ? 'Configured' : 'Not set'}
                    sx={{
                        fontFamily, fontSize: '0.6rem', fontWeight: 700, height: 22,
                        color: isConfigured ? colors.success : colors.warning,
                        bgcolor: isConfigured ? 'rgba(5,150,105,0.08)' : 'rgba(245,158,11,0.08)',
                        border: `1px solid ${isConfigured ? 'rgba(5,150,105,0.18)' : 'rgba(245,158,11,0.18)'}`,
                    }}
                />
            </Box>

            {/* ---- Stepper ---- */}
            <Stepper
                activeStep={step}
                alternativeLabel
                sx={{
                    mb: 2.5,
                    '& .MuiStepLabel-label': { fontFamily, fontSize: '0.7rem', mt: 0.5 },
                    '& .MuiStepLabel-label.Mui-active': { color: colors.accent, fontWeight: 700 },
                    '& .MuiStepLabel-label.Mui-completed': { color: colors.success },
                    '& .MuiStepIcon-root.Mui-active': { color: colors.accent },
                    '& .MuiStepIcon-root.Mui-completed': { color: colors.success },
                }}
            >
                {['Input source', 'Output source', 'Prompt'].map((label, i) => (
                    <Step key={label} completed={step > i}>
                        <StepLabel
                            onClick={() => { if (i < step) setStep(i); }}
                            sx={{ cursor: i < step ? 'pointer' : 'default' }}
                        >
                            {label}
                        </StepLabel>
                    </Step>
                ))}
            </Stepper>

            {/* ===================== STEP 1 — INPUT SOURCE ===================== */}
            {step === 0 && (<>
                {isConfigured && (
                    <CurrentSourcePill source={source} onRemove={handleRemove} disabled={submitting} />
                )}

                <FieldLabel>Source title</FieldLabel>
                <TextField
                    size="small"
                    fullWidth
                    required
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. Customers spreadsheet"
                    InputProps={{ sx: { fontFamily, fontSize: '0.82rem' } }}
                    sx={{ mb: 1.75 }}
                />

                <FieldLabel>Connect a source</FieldLabel>
                <ConnectorGrid value={activeType} onSelect={handlePickConnector} />

                {/* Upload */}
                {activeType === 'upload' && (
                    <Box sx={{ mb: 1.75 }}>
                        <FieldLabel>File</FieldLabel>
                        <UploadRow
                            file={pendingFile}
                            existingName={source?.source_type === 'upload' ? source.file_name : null}
                            hint="Excel, CSV, TXT, DOC/DOCX, PDF"
                            onPick={handlePickLocalFile}
                            inputRef={fileInputRef}
                            onChange={handleLocalFileChange}
                        />
                    </Box>
                )}

                {/* Google Drive / OneDrive */}
                {isDriveType(activeType) && (
                    <Box sx={{ mb: 1.75 }}>
                        <FieldLabel>How to pick the file</FieldLabel>
                        <PillToggle
                            value={driveMethod}
                            onChange={setDriveMethod}
                            options={[{ id: 'browse', label: 'Browse & pick' }, { id: 'url', label: 'Paste link' }]}
                        />
                        {driveMethod === 'browse' ? (
                            <DrivePickerArea
                                provider={driveProvider}
                                status={driveStatus}
                                statusLoading={driveStatusLoading}
                                onConnect={handleConnectDrive}
                                items={driveItems}
                                listLoading={driveListLoading}
                                breadcrumb={drivePath}
                                onEnterFolder={enterFolder}
                                onBreadcrumbClick={navigateToBreadcrumb}
                                onRefresh={() => loadDriveItems(drivePath[drivePath.length - 1]?.id || 'root')}
                                pickedFile={drivePickedFile}
                                onPickFile={setDrivePickedFile}
                            />
                        ) : (
                            <Box>
                                <TextField
                                    size="small"
                                    fullWidth
                                    value={driveUrl}
                                    onChange={(e) => setDriveUrl(e.target.value)}
                                    placeholder={driveProvider === 'google'
                                        ? 'https://drive.google.com/file/d/…/view'
                                        : 'https://onedrive.live.com/… or 1drv.ms/…'}
                                    InputProps={{ sx: { fontFamily, fontSize: '0.82rem' } }}
                                />
                                <Typography sx={{ fontFamily, fontSize: '0.66rem', color: colors.textTertiary, mt: 0.5 }}>
                                    Paste the share link of the <strong>file</strong> to read. Make sure {driveProvider === 'google' ? 'Google Drive' : 'OneDrive'} is connected so we can open it.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}

                {/* External URL */}
                {activeType === 'external_url' && (
                    <Box sx={{ mb: 1.75, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                        <Box>
                            <FieldLabel>URL</FieldLabel>
                            <TextField
                                size="small"
                                fullWidth
                                value={externalUrl}
                                onChange={(e) => setExternalUrl(e.target.value)}
                                placeholder="https://example.com/data.csv"
                                InputProps={{ sx: { fontFamily, fontSize: '0.82rem' } }}
                            />
                        </Box>
                        <Box>
                            <FieldLabel>File type</FieldLabel>
                            <TextField
                                select size="small" fullWidth
                                value={externalFileType}
                                onChange={(e) => setExternalFileType(e.target.value)}
                                InputProps={{ sx: { fontFamily, fontSize: '0.82rem' } }}
                            >
                                {['excel', 'csv', 'txt', 'doc', 'docx', 'pdf', 'other'].map((v) => (
                                    <MenuItem key={v} value={v} sx={{ fontFamily, fontSize: '0.82rem' }}>{v.toUpperCase()}</MenuItem>
                                ))}
                            </TextField>
                        </Box>
                    </Box>
                )}

                <FieldLabel>Description</FieldLabel>
                <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="What's in this source? Any column hints, business context, instructions for the agent…"
                    InputProps={{ sx: { fontFamily, fontSize: '0.8rem' } }}
                    sx={{ mb: 2 }}
                />
            </>)}

            {/* ===================== STEP 2 — OUTPUT SOURCE ===================== */}
            {step === 1 && (<>
                <Typography sx={{ fontFamily, fontSize: '0.74rem', color: colors.textSecondary, mb: 1.5 }}>
                    Where should the result of this agent be written?
                </Typography>

                <FieldLabel>Output source</FieldLabel>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                    gap: 1, mb: 1.75,
                }}>
                    {[
                        { id: 'same_as_input', label: 'Same as input source', description: 'Update the same file in place', icon: <SyncAltIcon sx={{ fontSize: 20 }} /> },
                        { id: 'separate', label: 'Separate output source', description: 'Write to a different destination', icon: <OutputOutlinedIcon sx={{ fontSize: 20 }} /> },
                    ].map((opt) => {
                        const active = outputMode === opt.id;
                        return (
                            <Box
                                key={opt.id}
                                onClick={() => setOutputMode(opt.id)}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1,
                                    px: 1.25, py: 1, borderRadius: '10px', cursor: 'pointer',
                                    bgcolor: active ? `${colors.accent}10` : 'rgba(0,0,0,0.02)',
                                    border: `1px solid ${active ? `${colors.accent}40` : 'rgba(0,0,0,0.06)'}`,
                                    transition: 'all 0.15s ease',
                                    '&:hover': {
                                        bgcolor: active ? `${colors.accent}14` : 'rgba(0,0,0,0.04)',
                                        borderColor: active ? `${colors.accent}55` : 'rgba(0,0,0,0.1)',
                                    },
                                }}
                            >
                                <Box sx={{
                                    width: 32, height: 32, borderRadius: '8px',
                                    bgcolor: active ? `${colors.accent}18` : 'rgba(0,0,0,0.04)',
                                    color: active ? colors.accent : colors.textSecondary,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                }}>
                                    {opt.icon}
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontFamily, fontSize: '0.78rem', fontWeight: 600, color: colors.textPrimary }} noWrap>
                                        {opt.label}
                                    </Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.66rem', color: colors.textTertiary }} noWrap>
                                        {opt.description}
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>

                {outputMode === 'same_as_input' ? (
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 0.75,
                        px: 1.25, py: 0.85, mb: 1.75, borderRadius: '8px',
                        bgcolor: 'rgba(0,0,0,0.02)', border: '1px solid rgba(0,0,0,0.06)',
                    }}>
                        <SyncAltIcon sx={{ fontSize: 15, color: colors.textTertiary }} />
                        <Typography sx={{ fontFamily, fontSize: '0.7rem', color: colors.textSecondary }}>
                            The result is written back to the same input file, keeping its format and all its columns &amp; rows
                            {source?.source_type === 'external_url'
                                ? ' — note: external-URL sources can\'t be written back, choose a separate output source.'
                                : '.'}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {/* Currently-saved separate output, if any. */}
                        {outputSaved?.source_type && (
                            <Box sx={{
                                display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.75,
                                px: 1.25, py: 1, borderRadius: '10px',
                                bgcolor: `${colors.accent}0a`, border: `1px solid ${colors.accent}24`,
                            }}>
                                <Box sx={{
                                    width: 32, height: 32, borderRadius: '8px',
                                    bgcolor: `${colors.accent}14`, color: colors.accent,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                }}>
                                    <OutputOutlinedIcon sx={{ fontSize: 18 }} />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontFamily, fontSize: '0.78rem', fontWeight: 600, color: colors.textPrimary }} noWrap>
                                        {outputSaved.title || outputSaved.file_name || outputSaved.drive_folder_name || outputSaved.drive_file_name || outputSaved.external_url || 'Output destination'}
                                    </Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.66rem', color: colors.textTertiary }}>
                                        Currently writing here · {(outputSaved.source_type || '').replace('_', ' ')}
                                        {outputSaved.drive_folder_id ? ' folder' : ''}
                                        {outputSaved.file_type ? ` · ${outputSaved.file_type.toUpperCase()}` : ''}
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        <FieldLabel>Output title</FieldLabel>
                        <TextField
                            size="small"
                            fullWidth
                            value={outputTitle}
                            onChange={(e) => setOutputTitle(e.target.value)}
                            placeholder="e.g. Overdue customers report"
                            InputProps={{ sx: { fontFamily, fontSize: '0.82rem' } }}
                            sx={{ mb: 1.75 }}
                        />

                        <FieldLabel>{outputSaved?.source_type ? 'Change destination' : 'Output destination'}</FieldLabel>
                        <ConnectorGrid value={oActiveType} onSelect={handlePickOConnector} />

                        {/* Upload */}
                        {oActiveType === 'upload' && (
                            <Box sx={{ mb: 1.75 }}>
                                <FieldLabel>File to write into</FieldLabel>
                                <UploadRow
                                    file={oPendingFile}
                                    existingName={outputSaved?.source_type === 'upload' ? outputSaved.file_name : null}
                                    hint="The agent overwrites this file with the result"
                                    onPick={handlePickOLocalFile}
                                    inputRef={oFileInputRef}
                                    onChange={handleOLocalFileChange}
                                    accept=".xlsx,.xls,.xlsm,.csv,.txt,.doc,.docx,.pdf,.json,.html"
                                />
                            </Box>
                        )}

                        {/* Google Drive / OneDrive */}
                        {isDriveType(oActiveType) && (
                            <Box sx={{ mb: 1.75 }}>
                                <FieldLabel>How to write the result</FieldLabel>
                                <PillToggle
                                    value={oDriveMethod}
                                    onChange={setODriveMethod}
                                    options={[{ id: 'url', label: 'Folder link' }, { id: 'browse', label: 'Overwrite a file' }]}
                                />
                                {oDriveMethod === 'url' ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            value={oDriveUrl}
                                            onChange={(e) => setODriveUrl(e.target.value)}
                                            placeholder={oDriveProvider === 'google'
                                                ? 'https://drive.google.com/drive/folders/…'
                                                : 'https://onedrive.live.com/… (folder)'}
                                            InputProps={{ sx: { fontFamily, fontSize: '0.82rem' } }}
                                        />
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                            <Box sx={{ minWidth: 130 }}>
                                                <FieldLabel>Output format</FieldLabel>
                                                <TextField
                                                    select size="small" fullWidth
                                                    value={oFolderFormat}
                                                    onChange={(e) => setOFolderFormat(e.target.value)}
                                                    InputProps={{ sx: { fontFamily, fontSize: '0.8rem' } }}
                                                >
                                                    {['xlsx', 'csv', 'docx', 'pdf', 'txt', 'json', 'html'].map((f) => (
                                                        <MenuItem key={f} value={f} sx={{ fontFamily, fontSize: '0.8rem' }}>{f.toUpperCase()}</MenuItem>
                                                    ))}
                                                </TextField>
                                            </Box>
                                            <Typography sx={{ fontFamily, fontSize: '0.66rem', color: colors.textTertiary, flex: 1, minWidth: 160, pb: 0.6 }}>
                                                The result is uploaded into this {oDriveProvider === 'google' ? 'Drive' : 'OneDrive'} folder, replacing a same-named file. Connect the account so we can write to it.
                                            </Typography>
                                        </Box>
                                    </Box>
                                ) : (
                                    <DrivePickerArea
                                        provider={oDriveProvider}
                                        status={oDriveStatus}
                                        statusLoading={oDriveStatusLoading}
                                        onConnect={handleConnectODrive}
                                        items={oDriveItems}
                                        listLoading={oDriveListLoading}
                                        breadcrumb={oDrivePath}
                                        onEnterFolder={oEnterFolder}
                                        onBreadcrumbClick={oNavigateBreadcrumb}
                                        onRefresh={() => loadODriveItems(oDrivePath[oDrivePath.length - 1]?.id || 'root')}
                                        pickedFile={oDrivePickedFile}
                                        onPickFile={setODrivePickedFile}
                                        pickHint="Pick the existing file to overwrite with the result"
                                    />
                                )}
                            </Box>
                        )}

                        {/* External URL */}
                        {oActiveType === 'external_url' && (
                            <Box sx={{ mb: 1.75, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                                <Box>
                                    <FieldLabel>URL</FieldLabel>
                                    <TextField
                                        size="small" fullWidth
                                        value={oExternalUrl}
                                        onChange={(e) => setOExternalUrl(e.target.value)}
                                        placeholder="https://example.com/endpoint"
                                        InputProps={{ sx: { fontFamily, fontSize: '0.82rem' } }}
                                    />
                                </Box>
                                <Box>
                                    <FieldLabel>File type</FieldLabel>
                                    <TextField
                                        select size="small" fullWidth
                                        value={oExternalFileType}
                                        onChange={(e) => setOExternalFileType(e.target.value)}
                                        InputProps={{ sx: { fontFamily, fontSize: '0.82rem' } }}
                                    >
                                        {['xlsx', 'csv', 'txt', 'docx', 'pdf', 'json', 'html', 'other'].map((v) => (
                                            <MenuItem key={v} value={v} sx={{ fontFamily, fontSize: '0.82rem' }}>{v.toUpperCase()}</MenuItem>
                                        ))}
                                    </TextField>
                                </Box>
                                <Typography sx={{ fontFamily, fontSize: '0.66rem', color: colors.textTertiary }}>
                                    Note: a plain web page can't be written back. Use upload or a drive destination if you want the result saved.
                                </Typography>
                            </Box>
                        )}

                        <FieldLabel>Output description</FieldLabel>
                        <TextField
                            size="small"
                            fullWidth
                            multiline
                            minRows={2}
                            value={outputDescription}
                            onChange={(e) => setOutputDescription(e.target.value)}
                            placeholder="What this output is for — columns to include, layout, who reads it…"
                            InputProps={{ sx: { fontFamily, fontSize: '0.8rem' } }}
                            sx={{ mb: 1 }}
                        />
                    </>
                )}
            </>)}

            {/* ===================== STEP 3 — PROMPT ===================== */}
            {step === 2 && (<>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <FieldLabel>One prompt for the whole task</FieldLabel>
                    <Typography
                        component="span"
                        onClick={() => setOutputPrompt(DEFAULT_OUTPUT_PROMPT)}
                        sx={{
                            fontFamily, fontSize: '0.66rem', fontWeight: 600, color: colors.accent,
                            cursor: 'pointer', mb: 0.6, flexShrink: 0,
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        Reset to example
                    </Typography>
                </Box>
                <Typography sx={{ fontFamily, fontSize: '0.72rem', color: colors.textTertiary, mb: 1 }}>
                    We've filled in an example below — edit it to describe your task. It covers BOTH what to read from the input and what to produce in the output, and the agent runs it whenever you ask.
                </Typography>
                <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={12}
                    value={outputPrompt}
                    onChange={(e) => setOutputPrompt(e.target.value)}
                    placeholder={"e.g. \"Read the Status column. Keep every existing column and row, and add a 'Days Late' column computed from the Due Date for rows marked 'Overdue'. Then write the result.\""}
                    InputProps={{ sx: { fontFamily, fontSize: '0.82rem', lineHeight: 1.5 } }}
                    sx={{ mb: 1 }}
                />
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1.25, mt: 0.5,
                    px: 1.25, py: 1, borderRadius: '10px',
                    bgcolor: 'rgba(0,0,0,0.02)', border: '1px solid rgba(0,0,0,0.06)',
                }}>
                    <SyncAltIcon sx={{ fontSize: 15, color: colors.textTertiary, flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontFamily, fontSize: '0.62rem', fontWeight: 700, color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Input → Output
                        </Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.7rem', color: colors.textSecondary }} noWrap>
                            {(source?.title || source?.file_name || source?.drive_file_name || 'input')} → {outputSummary()}
                        </Typography>
                    </Box>
                </Box>

                {/* ---- User Asks — example chat questions from the saved prompt ---- */}
                {(userAsksLoading || userAsks.length > 0) && (
                    <Box sx={{ mt: 2, pt: 1.75, borderTop: '1px dashed rgba(0,0,0,0.12)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.85 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, minWidth: 0 }}>
                                <AutoAwesomeIcon sx={{ fontSize: 15, color: colors.accent, flexShrink: 0 }} />
                                <Typography sx={{ fontFamily, fontSize: '0.74rem', fontWeight: 700, color: colors.textPrimary }}>
                                    Suggested Questions (Auto Generated)
                                </Typography>
                                <Typography sx={{ fontFamily, fontSize: '0.66rem', color: colors.textTertiary }} noWrap>
                                    — try these once you start chatting
                                </Typography>
                            </Box>
                            {!userAsksLoading && (
                                <Typography
                                    component="span"
                                    onClick={() => fetchUserAsks(outputPrompt)}
                                    sx={{
                                        fontFamily, fontSize: '0.66rem', fontWeight: 600, color: colors.accent,
                                        cursor: 'pointer', flexShrink: 0, '&:hover': { textDecoration: 'underline' },
                                    }}
                                >
                                    Regenerate
                                </Typography>
                            )}
                        </Box>
                        {userAsksLoading ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                                <CircularProgress size={14} sx={{ color: colors.accent }} />
                                <Typography sx={{ fontFamily, fontSize: '0.72rem', color: colors.textTertiary }}>
                                    Generating example questions for your task…
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                                {[RUN_TASK_QUESTION, ...userAsks.filter((q) => q !== RUN_TASK_QUESTION)].map((q, i) => {
                                    const primary = i === 0;
                                    return (
                                        <Box
                                            key={i}
                                            sx={{
                                                display: 'flex', alignItems: 'flex-start', gap: 0.85,
                                                px: 1.1, py: 0.7, borderRadius: '9px',
                                                bgcolor: primary ? `${colors.accent}1f` : `${colors.accent}0d`,
                                                border: `1px solid ${primary ? `${colors.accent}55` : `${colors.accent}26`}`,
                                            }}
                                        >
                                            {primary
                                                ? <AutoAwesomeIcon sx={{ fontSize: 14, color: colors.accent, mt: '3px', flexShrink: 0 }} />
                                                : <ChatBubbleOutlineIcon sx={{ fontSize: 14, color: colors.accent, mt: '3px', flexShrink: 0 }} />}
                                            <Typography sx={{ fontFamily, fontSize: '0.76rem', fontWeight: primary ? 700 : 400, color: primary ? colors.textPrimary : colors.textSecondary }}>
                                                {q}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </Box>
                )}
            </>)}

            {/* ---- Wizard footer ---- */}
            <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                mt: 2.5, pt: 2, borderTop: '1px solid rgba(0,0,0,0.08)',
            }}>
                <Button
                    size="small"
                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                    disabled={submitting || outputSubmitting}
                    sx={{
                        fontFamily, fontSize: '0.76rem', fontWeight: 600,
                        textTransform: 'none', color: colors.textSecondary,
                        visibility: step === 0 ? 'hidden' : 'visible',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                    }}
                >
                    ← Back
                </Button>

                {(() => {
                    const primarySx = {
                        fontFamily, fontSize: '0.78rem', fontWeight: 700,
                        textTransform: 'none', color: '#fff',
                        background: `linear-gradient(135deg, ${colors.accent}, ${colors.accent}cc)`,
                        borderRadius: '10px', px: 2.5, py: 0.95,
                        boxShadow: `0 4px 12px ${colors.accent}33`,
                        '&:hover': { background: colors.accent, opacity: 0.92 },
                        '&.Mui-disabled': { color: 'rgba(255,255,255,0.7)', background: 'rgba(0,0,0,0.18)' },
                    };
                    if (step === 0) {
                        return (
                            <Button size="small" variant="contained" sx={primarySx}
                                onClick={() => handleSubmit({ advance: true })}
                                disabled={submitting || !canSubmit()}>
                                {submitting ? 'Saving…' : 'Next: Output source →'}
                            </Button>
                        );
                    }
                    if (step === 1) {
                        return (
                            <Button size="small" variant="contained" sx={primarySx}
                                onClick={() => setStep(2)}>
                                Next: Prompt →
                            </Button>
                        );
                    }
                    return (
                        <Button size="small" variant="contained" sx={primarySx}
                            onClick={handleSaveOutput}
                            disabled={outputSubmitting || !outputPrompt.trim()}>
                            {outputSubmitting ? 'Saving…' : 'Save & Finish'}
                        </Button>
                    );
                })()}
            </Box>
        </Box>
    );
}

// ---------- Sub-components ----------

function FieldLabel({ children }) {
    return (
        <Typography sx={{
            fontFamily, fontSize: '0.62rem', fontWeight: 700,
            color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.6,
        }}>
            {children}
        </Typography>
    );
}

// App-launcher grid of connector tiles (icon on top, label below).
function ConnectorGrid({ value, onSelect }) {
    return (
        <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
            gap: 1, mb: 1.75,
        }}>
            {CONNECTORS.map((c) => {
                const active = value === c.id;
                return (
                    <Box
                        key={c.id}
                        onClick={() => onSelect(c.id)}
                        sx={{
                            position: 'relative',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75,
                            px: 1, py: 1.5, borderRadius: '12px', cursor: 'pointer', textAlign: 'center',
                            bgcolor: active ? `${colors.accent}0d` : 'rgba(0,0,0,0.02)',
                            border: `1px solid ${active ? `${colors.accent}55` : 'rgba(0,0,0,0.06)'}`,
                            transition: 'all 0.15s ease',
                            '&:hover': {
                                bgcolor: active ? `${colors.accent}14` : 'rgba(0,0,0,0.04)',
                                borderColor: active ? `${colors.accent}66` : 'rgba(0,0,0,0.12)',
                                transform: 'translateY(-1px)',
                            },
                        }}
                    >
                        {active && (
                            <CheckCircleOutlineIcon sx={{ fontSize: 16, color: colors.accent, position: 'absolute', top: 6, right: 6 }} />
                        )}
                        <Box sx={{
                            width: 44, height: 44, borderRadius: '12px',
                            bgcolor: '#fff', color: c.color,
                            boxShadow: active ? `0 2px 10px ${c.color}40` : '0 1px 3px rgba(0,0,0,0.08)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            {c.icon}
                        </Box>
                        <Box sx={{ minWidth: 0, width: '100%' }}>
                            <Typography sx={{ fontFamily, fontSize: '0.76rem', fontWeight: 700, color: colors.textPrimary }} noWrap>
                                {c.label}
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.62rem', color: colors.textTertiary }} noWrap>
                                {c.description}
                            </Typography>
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}

// Small pill segmented toggle (browse vs link, etc.).
function PillToggle({ value, options, onChange }) {
    return (
        <Box sx={{ display: 'inline-flex', bgcolor: 'rgba(0,0,0,0.04)', borderRadius: '10px', p: '3px', mb: 1.25 }}>
            {options.map((o) => {
                const active = value === o.id;
                return (
                    <Box
                        key={o.id}
                        onClick={() => onChange(o.id)}
                        sx={{
                            px: 1.5, py: 0.6, borderRadius: '8px', cursor: 'pointer',
                            bgcolor: active ? '#fff' : 'transparent',
                            boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                            transition: 'all 0.18s ease',
                        }}
                    >
                        <Typography sx={{
                            fontFamily, fontSize: '0.74rem', fontWeight: 600,
                            color: active ? colors.textPrimary : colors.textTertiary,
                        }}>
                            {o.label}
                        </Typography>
                    </Box>
                );
            })}
        </Box>
    );
}

// Reusable "choose a file" row (used by input + output upload connectors).
function UploadRow({ file, existingName, hint, onPick, inputRef, onChange, accept }) {
    return (
        <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.25, p: 1.25,
            border: '1px dashed rgba(0,0,0,0.16)', borderRadius: '12px',
            bgcolor: 'rgba(0,0,0,0.02)',
        }}>
            <Box sx={{
                width: 40, height: 40, borderRadius: '10px',
                bgcolor: `${colors.accent}14`, color: colors.accent,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
                {file
                    ? (FILE_TYPE_ICONS[guessFileType(file.name)] || FILE_TYPE_ICONS.other)
                    : <CloudUploadOutlinedIcon sx={{ fontSize: 22 }} />}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontFamily, fontSize: '0.78rem', fontWeight: 600, color: colors.textPrimary }} noWrap>
                    {file ? file.name : (existingName || 'No file selected')}
                </Typography>
                <Typography sx={{ fontFamily, fontSize: '0.66rem', color: colors.textTertiary }} noWrap>
                    {file ? `${formatBytes(file.size)} · Will upload on save` : hint}
                </Typography>
            </Box>
            <Button
                size="small"
                onClick={onPick}
                sx={{
                    fontFamily, fontSize: '0.74rem', fontWeight: 600,
                    textTransform: 'none', color: colors.accent,
                    border: `1px solid ${colors.accent}40`,
                    borderRadius: '10px', px: 1.5, py: 0.65,
                    '&:hover': { bgcolor: `${colors.accent}10` },
                }}
            >
                {file ? 'Change file' : 'Choose file'}
            </Button>
            <input
                ref={inputRef}
                type="file"
                accept={accept || '.xlsx,.xls,.xlsm,.csv,.txt,.doc,.docx,.pdf'}
                onChange={onChange}
                style={{ display: 'none' }}
            />
        </Box>
    );
}

function CurrentSourcePill({ source, onRemove, disabled }) {
    const icon = FILE_TYPE_ICONS[source?.file_type] || FILE_TYPE_ICONS.other;
    return (
        <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.75,
            px: 1.25, py: 1, borderRadius: '10px',
            bgcolor: `${colors.accent}0a`,
            border: `1px solid ${colors.accent}24`,
        }}>
            <Box sx={{
                width: 32, height: 32, borderRadius: '8px',
                bgcolor: `${colors.accent}14`, color: colors.accent,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
                {icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontFamily, fontSize: '0.78rem', fontWeight: 600, color: colors.textPrimary }} noWrap>
                    {source.title || source.file_name || source.drive_file_name || source.external_url || 'Source'}
                </Typography>
                <Typography sx={{ fontFamily, fontSize: '0.66rem', color: colors.textTertiary }}>
                    {source.source_type === 'upload' && `Uploaded · ${formatBytes(source.file_size)}`}
                    {source.source_type === 'google_drive' && `Google Drive · ${source.drive_file_name || source.drive_file_id}`}
                    {source.source_type === 'onedrive' && `OneDrive · ${source.drive_file_name || source.drive_file_id}`}
                    {source.source_type === 'external_url' && 'External URL'}
                    {source.file_type ? ` · ${source.file_type.toUpperCase()}` : ''}
                </Typography>
            </Box>
            {(source.drive_file_url || source.file_url) && (
                <Tooltip title={source.source_type === 'upload' ? 'Open file' : 'Open in browser'}>
                    <IconButton size="small" component="a" href={source.drive_file_url || source.file_url} target="_blank" rel="noopener">
                        <OpenInNewIcon sx={{ fontSize: 16, color: colors.textSecondary }} />
                    </IconButton>
                </Tooltip>
            )}
            <Tooltip title="Remove source">
                <IconButton size="small" onClick={onRemove} disabled={disabled}>
                    <DeleteOutlineIcon sx={{ fontSize: 16, color: colors.danger }} />
                </IconButton>
            </Tooltip>
        </Box>
    );
}

function DrivePickerArea({
    provider, status, statusLoading,
    onConnect, items, listLoading, breadcrumb, onEnterFolder, onBreadcrumbClick, onRefresh,
    pickedFile, onPickFile, pickHint,
}) {
    const providerLabel = provider === 'google' ? 'Google Drive' : 'OneDrive';

    if (statusLoading) {
        return <Skeleton variant="rounded" height={140} sx={{ borderRadius: '12px' }} />;
    }
    if (!status?.is_connected) {
        return (
            <Box sx={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: 0.75, p: 2.5, borderRadius: '12px',
                border: '1px dashed rgba(0,0,0,0.16)',
                bgcolor: 'rgba(0,0,0,0.02)', textAlign: 'center',
            }}>
                <LinkOffIcon sx={{ fontSize: 28, color: colors.textTertiary }} />
                <Typography sx={{ fontFamily, fontSize: '0.78rem', fontWeight: 600, color: colors.textPrimary }}>
                    {providerLabel} is not connected
                </Typography>
                <Typography sx={{ fontFamily, fontSize: '0.66rem', color: colors.textTertiary, maxWidth: 360 }}>
                    Connect your {providerLabel} account once. After that you can pick any file from it.
                </Typography>
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<LinkIcon sx={{ fontSize: '16px !important' }} />}
                    onClick={onConnect}
                    sx={{
                        mt: 0.5,
                        fontFamily, fontSize: '0.74rem', fontWeight: 600,
                        textTransform: 'none', color: '#fff',
                        background: `linear-gradient(135deg, ${colors.accent}, ${colors.accent}cc)`,
                        borderRadius: '10px', px: 2, py: 0.85,
                        '&:hover': { background: colors.accent, opacity: 0.92 },
                    }}
                >
                    Connect {providerLabel}
                </Button>
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 14, color: colors.success }} />
                <Typography sx={{ fontFamily, fontSize: '0.7rem', color: colors.textSecondary, flex: 1, minWidth: 0 }} noWrap>
                    Connected as <strong>{status?.email || providerLabel}</strong>
                </Typography>
                <Tooltip title="Refresh">
                    <IconButton size="small" onClick={onRefresh}>
                        <RefreshIcon sx={{ fontSize: 16, color: colors.textSecondary }} />
                    </IconButton>
                </Tooltip>
            </Box>

            {pickHint && (
                <Typography sx={{ fontFamily, fontSize: '0.66rem', color: colors.textTertiary, mb: 0.75 }}>
                    {pickHint}
                </Typography>
            )}

            <Breadcrumbs separator={<ChevronRightIcon sx={{ fontSize: 14, color: colors.textTertiary }} />}
                sx={{ mb: 0.75, fontFamily, fontSize: '0.7rem' }}
            >
                {breadcrumb.map((b, idx) => {
                    const last = idx === breadcrumb.length - 1;
                    if (last) {
                        return (
                            <Typography key={`${b.id}-${idx}`} sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, color: colors.textPrimary }}>
                                {b.name}
                            </Typography>
                        );
                    }
                    return (
                        <Link
                            key={`${b.id}-${idx}`}
                            component="button"
                            underline="hover"
                            onClick={() => onBreadcrumbClick(idx)}
                            sx={{ fontFamily, fontSize: '0.7rem', color: colors.textTertiary, cursor: 'pointer' }}
                        >
                            {b.name}
                        </Link>
                    );
                })}
            </Breadcrumbs>

            <Box sx={{
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: '10px',
                maxHeight: 280, overflowY: 'auto',
                bgcolor: '#fff',
            }}>
                {listLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={20} sx={{ color: colors.accent }} />
                    </Box>
                ) : items.length === 0 ? (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                        <Typography sx={{ fontFamily, fontSize: '0.74rem', color: colors.textTertiary }}>
                            (empty folder)
                        </Typography>
                    </Box>
                ) : (
                    items.map((it) => {
                        const isFolder = it.kind === 'folder';
                        const isPicked = !isFolder && pickedFile?.id === it.id;
                        const icon = isFolder
                            ? <FolderOutlinedIcon sx={{ fontSize: 18 }} />
                            : (FILE_TYPE_ICONS[it.file_type] || FILE_TYPE_ICONS.other);
                        return (
                            <Box
                                key={`${it.kind}-${it.id}`}
                                onClick={() => isFolder ? onEnterFolder(it) : onPickFile(it)}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1,
                                    px: 1.25, py: 0.85, cursor: 'pointer',
                                    borderBottom: '1px solid rgba(0,0,0,0.04)',
                                    bgcolor: isPicked ? `${colors.accent}10` : 'transparent',
                                    '&:hover': { bgcolor: isPicked ? `${colors.accent}14` : 'rgba(0,0,0,0.025)' },
                                    '&:last-of-type': { borderBottom: 'none' },
                                }}
                            >
                                <Box sx={{
                                    width: 28, height: 28, borderRadius: '8px',
                                    bgcolor: isFolder ? 'rgba(245,158,11,0.12)' : `${colors.accent}12`,
                                    color: isFolder ? colors.warning : colors.accent,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                }}>
                                    {icon}
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontFamily, fontSize: '0.78rem', fontWeight: 500, color: colors.textPrimary }} noWrap>
                                        {it.name}
                                    </Typography>
                                    {!isFolder && (
                                        <Typography sx={{ fontFamily, fontSize: '0.62rem', color: colors.textTertiary }} noWrap>
                                            {it.file_type ? it.file_type.toUpperCase() : ''}
                                            {it.size ? ` · ${formatBytes(it.size)}` : ''}
                                        </Typography>
                                    )}
                                </Box>
                                {isPicked && (
                                    <CheckCircleOutlineIcon sx={{ fontSize: 18, color: colors.accent, flexShrink: 0 }} />
                                )}
                                {isFolder && (
                                    <ChevronRightIcon sx={{ fontSize: 16, color: colors.textTertiary, flexShrink: 0 }} />
                                )}
                            </Box>
                        );
                    })
                )}
            </Box>

            {pickedFile && (
                <Typography sx={{
                    fontFamily, fontSize: '0.68rem', color: colors.success,
                    mt: 0.75, display: 'flex', alignItems: 'center', gap: 0.5,
                }}>
                    <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
                    Selected: <strong>{pickedFile.name}</strong>
                </Typography>
            )}
        </Box>
    );
}
